import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { PrivateUserInfoType, Vehicle } from '@otw/models'
import type { Lead } from '@otw/models/leads/lead'
import type { ShipmentPlan } from '@otw/models/logistics'
import { findMatchingClientUser } from 'views/SuperAdmin/Management/LeadManagement/util/customerUtils'
import { capResults } from 'hooks/search/filterSearchItems'
import {
  fetchClientUsers,
  fetchLeads,
  fetchMasterInventory,
  fetchShipmentPlans,
  fetchTransportTrucks,
  SEARCH_CACHE_STALE_MS,
  SEARCH_QUERY_KEYS,
  type TransportTruckSearchRow,
} from 'hooks/search/searchCaches'

interface UseClientUserSearchOptions {
  searchTerm: string
  enabled?: boolean
}

export type ClientUserSearchResult =
  | {
      type: 'client_shipment_plan'
      client: PrivateUserInfoType
      plan: ShipmentPlan
      transportTruckId: string
      transportVin?: string
      vehicleLabel?: string
    }
  | {
      type: 'client_lead'
      client: PrivateUserInfoType
      lead: Lead
    }

const normalize = (value?: string | null): string => (value || '').trim().toLowerCase()

export const clientUserMatchesSearchTerm = (
  user: PrivateUserInfoType,
  searchTerm: string,
): boolean => {
  const term = normalize(searchTerm)
  if (!term) return false

  const haystack = [
    user.first_name,
    user.last_name,
    user.displayName,
    user.company_name,
    user.email,
    `${user.first_name || ''} ${user.last_name || ''}`.trim(),
  ]
    .map(normalize)
    .filter(Boolean)

  return haystack.some((entry) => entry.includes(term))
}

export const clientUserMatchesStoredId = (
  user: PrivateUserInfoType,
  storedId?: string | null,
): boolean => {
  const id = storedId?.trim()
  if (!id) return false
  return user.id === id || (user.firestoreDocId != null && user.firestoreDocId === id)
}

export const getClientUserDisplayName = (user: PrivateUserInfoType): string => {
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim()
  return fullName || user.displayName || user.company_name || user.email || String(user.id)
}

const vehicleLabel = (vehicle?: Vehicle): string | undefined => {
  if (!vehicle) return undefined
  const ymm = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ').trim()
  const vin = vehicle.vin?.trim()
  return [vehicle.stock_number, ymm, vin ? `VIN ${vin}` : ''].filter(Boolean).join(' · ') || vehicle.id
}

export function buildClientSearchResults(
  clientUsers: PrivateUserInfoType[],
  plans: ShipmentPlan[],
  leads: Lead[],
  trucks: TransportTruckSearchRow[],
  vehicles: Vehicle[],
  searchTerm: string,
): ClientUserSearchResult[] {
  if (!searchTerm || searchTerm.trim().length < 2) {
    return []
  }

  const matchingClients = clientUsers.filter((user) =>
    clientUserMatchesSearchTerm(user, searchTerm),
  )
  if (matchingClients.length === 0) {
    return []
  }

  const truckVehicleIdByTruckId = new Map<string, string>()
  const truckVinByTruckId = new Map<string, string>()
  trucks.forEach((truck) => {
    if (truck.vehicle_id) truckVehicleIdByTruckId.set(truck.id, truck.vehicle_id)
    if (truck.vin?.trim()) truckVinByTruckId.set(truck.id, truck.vin.trim())
  })

  const vehiclesById = new Map(vehicles.map((vehicle) => [vehicle.id, vehicle]))
  const results: ClientUserSearchResult[] = []
  const seenPlanKeys = new Set<string>()
  const seenLeadIds = new Set<string>()

  plans.forEach((plan) => {
    const client = matchingClients.find((user) =>
      clientUserMatchesStoredId(user, plan.client_user_id),
    )
    if (!client || !plan.transport_truck_id) return

    const dedupeKey = `${plan.id}:${client.id}`
    if (seenPlanKeys.has(dedupeKey)) return
    seenPlanKeys.add(dedupeKey)

    const vehicleId = truckVehicleIdByTruckId.get(plan.transport_truck_id)
    results.push({
      type: 'client_shipment_plan',
      client,
      plan,
      transportTruckId: plan.transport_truck_id,
      transportVin: truckVinByTruckId.get(plan.transport_truck_id),
      vehicleLabel: vehicleId ? vehicleLabel(vehiclesById.get(vehicleId)) : undefined,
    })
  })

  leads.forEach((lead) => {
    const client = findMatchingClientUser(lead, matchingClients)
    if (!client || seenLeadIds.has(lead.id)) return
    if (!matchingClients.some((user) => user.id === client.id)) return

    seenLeadIds.add(lead.id)
    results.push({
      type: 'client_lead',
      client,
      lead,
    })
  })

  return capResults(results, 16)
}

export const useClientUserSearch = ({
  searchTerm,
  enabled = true,
}: UseClientUserSearchOptions) => {
  const { data: clientUsers = [], isPending: usersPending } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.clientUsers,
    queryFn: fetchClientUsers,
    enabled,
    staleTime: SEARCH_CACHE_STALE_MS,
  })
  const { data: plans = [], isPending: plansPending } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.shipmentPlans,
    queryFn: fetchShipmentPlans,
    enabled,
    staleTime: SEARCH_CACHE_STALE_MS,
  })
  const { data: leads = [], isPending: leadsPending } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.leads,
    queryFn: fetchLeads,
    enabled,
    staleTime: SEARCH_CACHE_STALE_MS,
  })
  const { data: trucks = [], isPending: trucksPending } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.transportTrucks,
    queryFn: fetchTransportTrucks,
    enabled,
    staleTime: SEARCH_CACHE_STALE_MS,
  })
  const { data: vehicles = [], isPending: vehiclesPending } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.inventory,
    queryFn: fetchMasterInventory,
    enabled,
    staleTime: SEARCH_CACHE_STALE_MS,
  })

  const data = useMemo(
    () => buildClientSearchResults(clientUsers, plans, leads, trucks, vehicles, searchTerm),
    [clientUsers, plans, leads, trucks, vehicles, searchTerm],
  )

  const isLoading =
    (usersPending && clientUsers.length === 0) ||
    (plansPending && plans.length === 0) ||
    (leadsPending && leads.length === 0) ||
    (trucksPending && trucks.length === 0) ||
    (vehiclesPending && vehicles.length === 0)

  return { data, isLoading }
}
